import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useState } from 'react';

interface ProjectData {
  cost: string;
  time: string;
  scope: string;
  summary: string;
  details: string[];
}

export default function ResultPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Datos del proyecto (vendrían del estado de navegación)
  const projectData: ProjectData = location.state?.projectData || {
    cost: '$5,000 - $8,000',
    time: '4-6 semanas',
    scope: 'Aplicación móvil completa',
    summary: 'Tu proyecto es una aplicación de chat con IA que incluye múltiples temas, progreso de conversación y experiencia de usuario optimizada.',
    details: [
      'Interfaz de usuario moderna y responsive',
      'Sistema de temas claro/oscuro',
      'Integración con IA conversacional',
      'Indicador de progreso por pasos',
      'Animaciones y transiciones suaves',
      'Pantalla de inicio personalizada'
    ]
  };

  const handleRestart = () => {
    navigate('/', { replace: true });
  };

  const generatePDF = () => {
    // Crear un contenedor temporal para el PDF
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Cotización de Proyecto - Paloma Studio</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            background: #fff;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 30px;
            border-bottom: 3px solid #8B5CF6;
          }
          
          .logo {
            font-size: 32px;
            font-weight: bold;
            background: linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 10px;
          }
          
          .header h1 {
            font-size: 28px;
            color: #1a1a1a;
            margin-bottom: 10px;
          }
          
          .header .date {
            color: #666;
            font-size: 14px;
          }
          
          .section {
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          
          .section-title {
            font-size: 20px;
            color: #8B5CF6;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px solid #f0f0f0;
          }
          
          .info-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
            margin-bottom: 30px;
          }
          
          .info-card {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            padding: 20px;
            border-radius: 12px;
            text-align: center;
            border: 1px solid #dee2e6;
          }
          
          .info-card .icon {
            font-size: 32px;
            margin-bottom: 10px;
          }
          
          .info-card .label {
            font-size: 12px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 5px;
          }
          
          .info-card .value {
            font-size: 18px;
            font-weight: bold;
            color: #1a1a1a;
          }
          
          .summary-box {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 12px;
            border-left: 4px solid #8B5CF6;
            margin-bottom: 20px;
          }
          
          .summary-box p {
            color: #495057;
            line-height: 1.8;
          }
          
          .features-list {
            list-style: none;
            padding: 0;
          }
          
          .features-list li {
            padding: 12px 0;
            padding-left: 30px;
            position: relative;
            border-bottom: 1px solid #f0f0f0;
          }
          
          .features-list li:last-child {
            border-bottom: none;
          }
          
          .features-list li:before {
            content: "✓";
            position: absolute;
            left: 0;
            color: #10B981;
            font-weight: bold;
            font-size: 18px;
          }
          
          .footer {
            margin-top: 50px;
            padding-top: 30px;
            border-top: 2px solid #f0f0f0;
            text-align: center;
          }
          
          .footer .validity {
            background: #FEF3C7;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
            border: 1px solid #FCD34D;
          }
          
          .footer .validity p {
            color: #92400E;
            font-weight: 500;
          }
          
          .footer .contact {
            color: #666;
            font-size: 14px;
          }
          
          .footer .branding {
            margin-top: 20px;
            font-size: 12px;
            color: #999;
          }
          
          @media print {
            body {
              padding: 20px;
            }
            
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">Paloma Studio</div>
          <h1>Cotización de Proyecto</h1>
          <p class="date">${currentDate}</p>
        </div>
        
        <div class="section">
          <h2 class="section-title">Resumen Ejecutivo</h2>
          <div class="info-grid">
            <div class="info-card">
              <div class="icon">💰</div>
              <div class="label">Coste Estimado</div>
              <div class="value">${projectData.cost}</div>
            </div>
            <div class="info-card">
              <div class="icon">⏱️</div>
              <div class="label">Tiempo de Desarrollo</div>
              <div class="value">${projectData.time}</div>
            </div>
            <div class="info-card">
              <div class="icon">📋</div>
              <div class="label">Alcance</div>
              <div class="value">${projectData.scope}</div>
            </div>
          </div>
        </div>
        
        <div class="section">
          <h2 class="section-title">Descripción del Proyecto</h2>
          <div class="summary-box">
            <p>${projectData.summary}</p>
          </div>
        </div>
        
        <div class="section">
          <h2 class="section-title">Características Incluidas</h2>
          <ul class="features-list">
            ${projectData.details.map(detail => `<li>${detail}</li>`).join('')}
          </ul>
        </div>
        
        <div class="footer">
          <div class="validity">
            <p>⚠️ Esta cotización es válida por 30 días a partir de la fecha de emisión</p>
          </div>
          <div class="contact">
            <p>Para más información o consultas, por favor contáctanos</p>
          </div>
          <div class="branding">
            <p>Generado por Paloma Studio - Soluciones Digitales Profesionales</p>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Esperar a que se cargue el contenido antes de imprimir
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  const generateTXT = () => {
    const currentDate = new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const txtContent = `
═══════════════════════════════════════════════════════════════════
                        PALOMA STUDIO
                   COTIZACIÓN DE PROYECTO
═══════════════════════════════════════════════════════════════════

Fecha de emisión: ${currentDate}

───────────────────────────────────────────────────────────────────
RESUMEN EJECUTIVO
───────────────────────────────────────────────────────────────────

💰 COSTE ESTIMADO
   ${projectData.cost}

⏱️ TIEMPO DE DESARROLLO
   ${projectData.time}

📋 ALCANCE DEL PROYECTO
   ${projectData.scope}

───────────────────────────────────────────────────────────────────
DESCRIPCIÓN DEL PROYECTO
───────────────────────────────────────────────────────────────────

${projectData.summary}

───────────────────────────────────────────────────────────────────
CARACTERÍSTICAS INCLUIDAS
───────────────────────────────────────────────────────────────────

${projectData.details.map((detail, index) => `${index + 1}. ✓ ${detail}`).join('\n')}

───────────────────────────────────────────────────────────────────
CONDICIONES
───────────────────────────────────────────────────────────────────

⚠️  VALIDEZ: Esta cotización es válida por 30 días a partir de la 
    fecha de emisión.

📧  CONTACTO: Para más información o consultas, por favor contáctanos.

💼  PRÓXIMOS PASOS: Una vez aceptada la cotización, procederemos con:
    • Firma del contrato
    • Pago inicial según términos acordados
    • Inicio del desarrollo
    • Reuniones de seguimiento periódicas

───────────────────────────────────────────────────────────────────
NOTAS IMPORTANTES
───────────────────────────────────────────────────────────────────

• Los costes pueden variar según cambios en los requisitos
• El tiempo estimado asume disponibilidad de recursos y feedback oportuno
• Se requiere aprobación del cliente en hitos clave del proyecto
• Incluye soporte post-lanzamiento según términos del contrato

═══════════════════════════════════════════════════════════════════
Generado por Paloma Studio - Soluciones Digitales Profesionales
═══════════════════════════════════════════════════════════════════
    `.trim();

    // Crear un blob y descargarlo
    const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Cotizacion_Proyecto_${new Date().getTime()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = (format: string) => {
    if (format === 'pdf') {
      generatePDF();
    } else if (format === 'txt') {
      generateTXT();
    }
    setShowExportMenu(false);
  };

  const handleSendEmail = () => {
    // Simular envío de email
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  };

  return (
    <div className={`min-h-screen ${
      theme === 'dark'
        ? 'bg-gradient-to-br from-[#0A0118] via-[#1A0B2E] to-[#0A0118]'
        : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
    }`}>
      {/* Efectos de fondo */}
      {theme === 'dark' && (
        <>
          <div className="fixed top-20 left-10 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="fixed bottom-20 right-10 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </>
      )}

      {/* Contenido */}
      <div className="relative max-w-md mx-auto px-6 py-12">
        {/* Header con icono de éxito */}
        <div className="text-center mb-8">
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-green-500/20 via-emerald-500/20 to-teal-500/20'
              : 'bg-gradient-to-br from-green-100 via-emerald-100 to-teal-100'
          }`}>
            <i className={`ri-check-line text-4xl ${
              theme === 'dark' ? 'text-green-400' : 'text-green-600'
            }`}></i>
          </div>
          <h1 className={`text-2xl font-bold mb-2 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            ¡Cotización Lista!
          </h1>
          <p className={`text-sm ${
            theme === 'dark' ? 'text-white/60' : 'text-gray-500'
          }`}>
            Aquí está el resumen de tu proyecto
          </p>
        </div>

        {/* Tarjeta de resumen */}
        <div className={`rounded-3xl p-6 mb-6 ${
          theme === 'dark'
            ? 'bg-white/5 backdrop-blur-sm border border-white/10'
            : 'bg-white shadow-lg border border-gray-100'
        }`}>
          {/* Datos clave */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <div className={`w-12 h-12 mx-auto rounded-2xl flex items-center justify-center mb-2 ${
                theme === 'dark'
                  ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20'
                  : 'bg-gradient-to-br from-blue-100 to-purple-100'
              }`}>
                <i className={`ri-money-dollar-circle-line text-xl ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`}></i>
              </div>
              <p className={`text-xs mb-1 ${
                theme === 'dark' ? 'text-white/50' : 'text-gray-400'
              }`}>
                Coste
              </p>
              <p className={`text-sm font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {projectData.cost}
              </p>
            </div>

            <div className="text-center">
              <div className={`w-12 h-12 mx-auto rounded-2xl flex items-center justify-center mb-2 ${
                theme === 'dark'
                  ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20'
                  : 'bg-gradient-to-br from-purple-100 to-pink-100'
              }`}>
                <i className={`ri-time-line text-xl ${
                  theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                }`}></i>
              </div>
              <p className={`text-xs mb-1 ${
                theme === 'dark' ? 'text-white/50' : 'text-gray-400'
              }`}>
                Tiempo
              </p>
              <p className={`text-sm font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {projectData.time}
              </p>
            </div>

            <div className="text-center">
              <div className={`w-12 h-12 mx-auto rounded-2xl flex items-center justify-center mb-2 ${
                theme === 'dark'
                  ? 'bg-gradient-to-br from-pink-500/20 to-orange-500/20'
                  : 'bg-gradient-to-br from-pink-100 to-orange-100'
              }`}>
                <i className={`ri-layout-grid-line text-xl ${
                  theme === 'dark' ? 'text-pink-400' : 'text-pink-600'
                }`}></i>
              </div>
              <p className={`text-xs mb-1 ${
                theme === 'dark' ? 'text-white/50' : 'text-gray-400'
              }`}>
                Alcance
              </p>
              <p className={`text-sm font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {projectData.scope}
              </p>
            </div>
          </div>

          {/* Resumen */}
          <div className={`rounded-2xl p-4 mb-4 ${
            theme === 'dark'
              ? 'bg-white/5 border border-white/5'
              : 'bg-gray-50 border border-gray-100'
          }`}>
            <p className={`text-sm leading-relaxed ${
              theme === 'dark' ? 'text-white/80' : 'text-gray-700'
            }`}>
              {projectData.summary}
            </p>
          </div>

          {/* Detalles */}
          <div className="space-y-2">
            <p className={`text-xs font-semibold mb-3 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              Características incluidas:
            </p>
            {projectData.details.map((detail, index) => (
              <div key={index} className="flex items-start gap-2">
                <i className={`ri-checkbox-circle-fill text-sm mt-0.5 ${
                  theme === 'dark' ? 'text-green-400' : 'text-green-600'
                }`}></i>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-white/70' : 'text-gray-600'
                }`}>
                  {detail}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Botones de acción */}
        <div className="space-y-3">
          {/* Botón Reiniciar */}
          <button
            onClick={handleRestart}
            className={`w-full py-4 rounded-2xl font-semibold transition-all ${
              theme === 'dark'
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg hover:shadow-purple-500/30'
                : 'bg-gradient-to-r from-purple-400 to-pink-400 text-white hover:shadow-lg hover:shadow-purple-400/30'
            }`}
          >
            <i className="ri-refresh-line mr-2"></i>
            Nueva Cotización
          </button>

          {/* Botón Exportar */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className={`w-full py-4 rounded-2xl font-semibold transition-all ${
                theme === 'dark'
                  ? 'bg-white/10 text-white border border-white/20 hover:bg-white/15'
                  : 'bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 shadow-sm'
              }`}
            >
              <i className="ri-download-line mr-2"></i>
              Exportar
            </button>

            {/* Menú de exportación */}
            {showExportMenu && (
              <div className={`absolute bottom-full left-0 right-0 mb-2 rounded-2xl overflow-hidden ${
                theme === 'dark'
                  ? 'bg-white/10 backdrop-blur-xl border border-white/20'
                  : 'bg-white border border-gray-200 shadow-xl'
              }`}>
                <button
                  onClick={() => handleExport('pdf')}
                  className={`w-full py-3 px-4 text-left transition-colors ${
                    theme === 'dark'
                      ? 'text-white hover:bg-white/10'
                      : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-file-pdf-line mr-2"></i>
                  Exportar como PDF
                </button>
                <button
                  onClick={() => handleExport('txt')}
                  className={`w-full py-3 px-4 text-left transition-colors ${
                    theme === 'dark'
                      ? 'text-white hover:bg-white/10'
                      : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-file-text-line mr-2"></i>
                  Exportar como TXT
                </button>
              </div>
            )}
          </div>

          {/* Botón Enviar por correo */}
          <button
            onClick={handleSendEmail}
            disabled={emailSent}
            className={`w-full py-4 rounded-2xl font-semibold transition-all ${
              emailSent
                ? theme === 'dark'
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-green-100 text-green-700 border border-green-200'
                : theme === 'dark'
                  ? 'bg-white/10 text-white border border-white/20 hover:bg-white/15'
                  : 'bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 shadow-sm'
            }`}
          >
            {emailSent ? (
              <>
                <i className="ri-check-line mr-2"></i>
                Enviado
              </>
            ) : (
              <>
                <i className="ri-mail-send-line mr-2"></i>
                Enviar por Correo
              </>
            )}
          </button>
        </div>

        {/* Nota al pie */}
        <p className={`text-center text-xs mt-6 ${
          theme === 'dark' ? 'text-white/40' : 'text-gray-400'
        }`}>
          Esta cotización es válida por 30 días
        </p>
      </div>
    </div>
  );
}
